// ConfigCamposMovil.jsx
// Ruta: frontend/src/pages/ConfigCamposMovil.jsx

import { useState, useEffect, useCallback } from "react";
import { useTheme } from "../components/ThemeProvider";
import api from "../services/api";
import usePermiso from "../hooks/usePermiso";

// ─── Constantes ───────────────────────────────────────────────────────────────

const STORAGE_KEY = "vitalcasa_campos_config_v2";
const TIPOS_POR_PAGINA = 35;

const TA_COLS = ["ta1", "ta2", "ta3", "ta4", "ta5"];

const PAGINAS_ESPECIALES = ["P", "2", "3", "4", "5", "6", "7", "8", "9", "10", "N-2", "N-1", "N"];

const ordenPagina = (p) =>
  p === "P" ? 0 : p === "N-2" ? 97 : p === "N-1" ? 98 : p === "N" ? 99 : parseInt(p) || 50;
const PAGINA_LABELS = {
  P:    "P — Primera (fija)",
  "N-2":"N-2 — Propietarios",
  "N-1":"N-1 — Firma",
  N:    "N — Resumen (última)",
};

const OPCIONES = [
  { val: "O", emoji: "●", color: "#ff0000", title: "Obligatorio" },
  { val: "P", emoji: "●", color: "#22c55e", title: "Principal"   },
  { val: "S", emoji: "●", color: "#eab308", title: "Secundario"  },
  { val: "N", emoji: "●", color: "#e2e8f0", title: "No aplica"   },
];

const GRUPOS_COLORES = {
  "Principales":  "#2563eb",
  "Generales":    "#059669",
  "Distribución": "#7c3aed",
  "Equipamiento": "#db2777",
  "Ubicación":    "#0891b2",
  "Económicos":   "#d97706",
  "Dirección":    "#ea580c",
  "Urbanismo":    "#0d9488",
  "Multimedia":   "#0284c7",
  "Propietarios": "#475569",
  "Internos":     "#4f46e5",
  "Resumen":      "#334155",
};

// Mapping tipo de inmueble → columna TA (sincronizado con app.js)
const TIPO_A_TA = {
  'Chalet':'ta1','Casa':'ta1','Piso':'ta1','Apartamento':'ta1','Ático':'ta1',
  'Bungalow':'ta1','Casa de campo':'ta1','Estudio':'ta1','Chalet sobre plano':'ta1',
  'Chalet (sobre plano)':'ta1','Pareado':'ta1','Planta Baja de Casa':'ta1','Primer piso de casa':'ta1',
  'Parcela urbana':'ta2','Parcela rústica':'ta2','Solar':'ta2','Parcela agraria':'ta2',
  'Local comercial':'ta3','Oficina':'ta3','Restaurante':'ta3','Hotel':'ta3','Nave':'ta3','Edificio':'ta3',
  'Garaje':'ta4','Trastero':'ta4','Plaza de parking':'ta4','Plaza de Garaje':'ta4',
};

// ─── Componente principal ─────────────────────────────────────────────────────

export default function ConfigCamposMovil() {
  const { theme } = useTheme();
  const isDark = theme.name === "dark";
  const { puede } = usePermiso();
  const vePaginas = puede("cfg_movil_paginas", "ver");
  const modPaginas = puede("cfg_movil_paginas", "modificar");
  const vePrioridad = puede("cfg_movil_prioridad", "ver");
  const modPrioridad = puede("cfg_movil_prioridad", "modificar");
  const vePorTa = puede("cfg_movil_por_ta", "ver");
  const modPorTa = puede("cfg_movil_por_ta", "modificar");

  // ── Estado ─────────────────────────────────────────────────────────────────
  const [campos,    setCampos]    = useState([]);
  const [tipos,     setTipos]     = useState([]);
  const [cargando,  setCargando]  = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [guardado,  setGuardado]  = useState(false);
  const [error,     setError]     = useState("");
  const [cambiosSinGuardar, setCambiosSinGuardar] = useState(false);

  // Filtros UI
  const [busqueda,      setBusqueda]      = useState("");
  const [grupoFiltro,   setGrupoFiltro]   = useState("todos");
  const [taColFiltro,   setTaColFiltro]   = useState("todos");
  const [paginaFiltro,  setPaginaFiltro]  = useState("todas");
  const [taFiltro,      setTaFiltro]      = useState("todos");
  const [visibilidadFiltro, setVisibilidadFiltro] = useState("todos");
  const [paginaTipos,   setPaginaTipos]   = useState(0);
  const [tiposVisibles, setTiposVisibles] = useState([]);
  const [panelTipos,    setPanelTipos]    = useState(false);
  /** Filtro multi-selección por tipo de inmueble (vacío = todos). Afecta columnas y filtro O/P/S/N. */
  const [tiposInmuebleFiltro, setTiposInmuebleFiltro] = useState([]);
  const [panelTiposFiltro, setPanelTiposFiltro] = useState(false);

  // ── Carga inicial ──────────────────────────────────────────────────────────
  useEffect(() => { cargar(); }, []);

  const cargar = async () => {
    setCargando(true);
    setError("");
    try {
      const [rTipos, rCampos] = await Promise.all([
        api.get("/movil-campos/tipos"),
        api.get("/movil-campos"),
      ]);
      const listaTipos = (rTipos.data.data || []).map(t => t.nombre ?? t);
      const listaCampos = rCampos.data.data || [];

      setTipos(listaTipos);
      setCampos(listaCampos);
      setTiposVisibles(listaTipos.slice(0, Math.min(TIPOS_POR_PAGINA, listaTipos.length)));
      localStorage.setItem(STORAGE_KEY, JSON.stringify(listaCampos));
      setCambiosSinGuardar(false);
    } catch (err) {
      setError("Error al cargar: " + (err.response?.data?.error || err.message));
      try {
        const cached = localStorage.getItem(STORAGE_KEY);
        if (cached) setCampos(JSON.parse(cached));
      } catch {}
    } finally {
      setCargando(false);
    }
  };

  // ── Mutaciones locales ─────────────────────────────────────────────────────

  const cambiarVisibilidad = useCallback((campoId, tipo, valor) => {
    setCampos(prev => prev.map(f =>
      f.id === campoId ? { ...f, visibilidad: { ...(f.visibilidad || {}), [tipo]: valor } } : f
    ));
    setCambiosSinGuardar(true);
    setGuardado(false);
  }, []);

  const cambiarTAx = useCallback((campoId, cual, valor) => {
    setCampos(prev => prev.map(f =>
      f.id === campoId ? { ...f, [cual]: valor || null } : f
    ));
    setCambiosSinGuardar(true);
    setGuardado(false);
  }, []);

  // Con filtro TA1–TA5: edita orden/línea solo de ese TA (null = sigue el global).
  // Con "Todos los TA": edita el orden/línea global (no toca los overrides por TA).
  const taOrdenActivo = TA_COLS.includes(taColFiltro) ? taColFiltro : null;

  const ordenEfectivo = useCallback((f) => {
    if (taOrdenActivo) {
      const v = f[`orden_${taOrdenActivo}`];
      if (v != null && v !== "") return Number(v);
    }
    return f.orden ?? "";
  }, [taOrdenActivo]);

  const lineaEfectiva = useCallback((f) => {
    if (taOrdenActivo) {
      const v = f[`linea_${taOrdenActivo}`];
      if (v != null && v !== "") return Number(v);
    }
    return f.linea ?? "";
  }, [taOrdenActivo]);

  const cambiarOrden = useCallback((campoId, valor) => {
    const num = valor === "" ? null : parseInt(valor, 10);
    const safe = Number.isNaN(num) ? null : num;
    setCampos(prev => prev.map(f => {
      if (f.id !== campoId) return f;
      if (taOrdenActivo) return { ...f, [`orden_${taOrdenActivo}`]: safe };
      return { ...f, orden: safe };
    }));
    setCambiosSinGuardar(true);
    setGuardado(false);
  }, [taOrdenActivo]);

  const cambiarLinea = useCallback((campoId, valor) => {
    const num = valor === "" ? null : parseInt(valor, 10);
    const safe = Number.isNaN(num) ? null : num;
    setCampos(prev => prev.map(f => {
      if (f.id !== campoId) return f;
      if (taOrdenActivo) return { ...f, [`linea_${taOrdenActivo}`]: safe };
      return { ...f, linea: safe };
    }));
    setCambiosSinGuardar(true);
    setGuardado(false);
  }, [taOrdenActivo]);

  const cambiarTipoInput = useCallback((campoId, valor) => {
    setCampos(prev => prev.map(f =>
      f.id === campoId ? { ...f, campo_tipo_input: valor } : f
    ));
    setCambiosSinGuardar(true);
    setGuardado(false);
  }, []);

  // ── Guardar en BD ──────────────────────────────────────────────────────────

  const guardar = async () => {
    setGuardando(true);
    setError("");
    try {
      await api.post("/movil-campos/bulk", {
        campos: campos.map(f => ({
          id:               f.id,
          visibilidad:      f.visibilidad || {},
          pagina_movil:     f.pagina_movil || null,
          ta1:              f.ta1 || null,
          ta2:              f.ta2 || null,
          ta3:              f.ta3 || null,
          ta4:              f.ta4 || null,
          ta5:              f.ta5 || null,
          orden:            f.orden ?? 0,
          linea:            f.linea ?? null,
          orden_ta1:        f.orden_ta1 ?? null,
          orden_ta2:        f.orden_ta2 ?? null,
          orden_ta3:        f.orden_ta3 ?? null,
          orden_ta4:        f.orden_ta4 ?? null,
          orden_ta5:        f.orden_ta5 ?? null,
          linea_ta1:        f.linea_ta1 ?? null,
          linea_ta2:        f.linea_ta2 ?? null,
          linea_ta3:        f.linea_ta3 ?? null,
          linea_ta4:        f.linea_ta4 ?? null,
          linea_ta5:        f.linea_ta5 ?? null,
          campo_tipo_input: f.campo_tipo_input || null,
          subcampos:        f.subcampos ?? null,
        })),
      });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(campos));
      setCambiosSinGuardar(false);
      setGuardado(true);
      setTimeout(() => setGuardado(false), 2500);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.error || err.message;
      if (status === 502 || status === 504) {
        setError("Error al guardar: el servidor tardó demasiado (proxy). Reintenta en unos segundos; si persiste, avisa.");
      } else {
        setError("Error al guardar: " + detail);
      }
    } finally {
      setGuardando(false);
    }
  };

  // ── Datos derivados ────────────────────────────────────────────────────────

  const grupos = ["todos", ...Array.from(new Set(campos.map(f => f.campo_grupo).filter(Boolean)))];

  const paginas = ["todas", ...Array.from(new Set(
    (taColFiltro !== "todos"
      ? campos.map(f => f[taColFiltro])
      : campos.flatMap(f => TA_COLS.map(col => f[col]))
    ).filter(Boolean)
  )).sort((a, b) => ordenPagina(a) - ordenPagina(b))];

  const tasDisponibles = ["todos", "sin-ta", "ta1", "ta2", "ta3", "ta4", "ta5"];

  const camposFiltrados = campos.filter(f => {
    if (busqueda.trim()) {
      const q = busqueda.toLowerCase();
      if (!f.campo_label?.toLowerCase().includes(q) && !f.campo_key?.toLowerCase().includes(q)) return false;
    }
    if (grupoFiltro !== "todos" && f.campo_grupo !== grupoFiltro) return false;
    if (paginaFiltro !== "todas") {
      const colsPagina = taColFiltro !== "todos" ? [taColFiltro] : TA_COLS;
      if (!colsPagina.some(col => f[col] === paginaFiltro)) return false;
    }
    if (taFiltro !== "todos") {
      const tieneTA = f.ta1 || f.ta2 || f.ta3 || f.ta4 || f.ta5;
      if (taFiltro === "sin-ta" && tieneTA) return false;
      if (taFiltro !== "sin-ta" && !f[taFiltro]) return false;
    }
    if (visibilidadFiltro !== "todos") {
      // Mostrar campos donde AL MENOS UN tipo visible (y del filtro de tipos) tenga esa visibilidad
      const tiposActuales = (taColFiltro !== "todos"
        ? tiposVisibles.filter(t => (TIPO_A_TA[t] || 'ta1') === taColFiltro)
        : tiposVisibles
      ).filter(t => tiposInmuebleFiltro.length === 0 || tiposInmuebleFiltro.includes(t));
      const tieneVal = tiposActuales.some(t => (f.visibilidad?.[t] ?? "N") === visibilidadFiltro);
      if (!tieneVal) return false;
    }
    return true;
  });

  const camposAgrupados = (() => {
    const mapaGrupos = new Map();
    camposFiltrados.forEach(f => {
      const g = f.campo_grupo || "";
      if (!mapaGrupos.has(g)) mapaGrupos.set(g, []);
      mapaGrupos.get(g).push(f);
    });
    return Array.from(mapaGrupos.values()).flat();
  })();

  const tiposTrasFiltroInmueble = tiposVisibles.filter(t =>
    (taColFiltro === "todos" || (TIPO_A_TA[t] || 'ta1') === taColFiltro) &&
    (tiposInmuebleFiltro.length === 0 || tiposInmuebleFiltro.includes(t))
  );
  const totalPagTipos = Math.ceil(tiposTrasFiltroInmueble.length / TIPOS_POR_PAGINA) || 1;
  const tiposEnTabla  = tiposTrasFiltroInmueble
    .slice(paginaTipos * TIPOS_POR_PAGINA, (paginaTipos + 1) * TIPOS_POR_PAGINA);

  /* Nombre(1) + tipos(prioridad) + orden/línea(por TA) + tipo(1, siempre) + TA1-5(páginas) */
  const totalColumnasTabla = 1
    + (vePrioridad ? tiposEnTabla.length : 0)
    + (vePorTa ? 2 : 0)
    + 1
    + (vePaginas ? 5 : 0);

  // ── Tokens de color ────────────────────────────────────────────────────────
  const bgPage   = isDark ? "#0a0c12" : "#f1f5f9";
  const bgPanel  = isDark ? "#0f1117" : "#ffffff";
  const bgCard   = isDark ? "#12151e" : "#ffffff";
  const bgHead   = isDark ? "#0d1018" : "#f8fafc";
  const txtMain  = isDark ? "#f1f5f9" : "#1e293b";
  const txtMuted = isDark ? "#94a3b8" : "#64748b";
  const border   = isDark ? "#252d42" : "#e2e8f0";
  const bgInput  = isDark ? "#161b28" : "#f8fafc";

  // ── Render: cargando ───────────────────────────────────────────────────────
  if (cargando) return (
    <div style={{ padding: 40, textAlign: "center", color: txtMuted, fontSize: 14, background: bgPage, minHeight: "100vh" }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>📱</div>
      Cargando configuración de campos...
    </div>
  );

  // ── Render principal ───────────────────────────────────────────────────────
  return (
    <div style={{ padding: "16px", background: bgPage, minHeight: "100vh" }}>

      {/* ── CABECERA ──────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 14, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 10, alignItems: "flex-start" }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 800, color: txtMain, margin: 0, letterSpacing: "-0.02em" }}>
            📱 Configuración campos — App Móvil
          </h1>
          <p style={{ color: txtMuted, marginTop: 4, fontSize: 12, margin: "4px 0 0" }}>
            {campos.length} campos · {tipos.length} tipos de inmueble · {camposFiltrados.length} mostrados
            {cambiosSinGuardar && (
              <span style={{ color: "#f59e0b", marginLeft: 12, fontWeight: 700 }}>● Cambios sin guardar</span>
            )}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button onClick={cargar} style={{
            padding: "7px 12px", borderRadius: 8, border: `1px solid ${border}`,
            background: bgInput, color: txtMuted, fontSize: 12, cursor: "pointer",
          }}>↺ Recargar</button>
          <button onClick={guardar} disabled={guardando} style={{
            padding: "8px 20px", borderRadius: 8, border: "none",
            background: guardado ? "#22c55e" : cambiosSinGuardar ? "#f59e0b" : "#2563eb",
            color: "#fff", fontSize: 13, fontWeight: 700,
            cursor: guardando ? "not-allowed" : "pointer",
            transition: "background 0.3s",
            boxShadow: "0 2px 8px rgba(37,99,235,0.25)",
          }}>
            {guardando ? "Guardando..." : guardado ? "✅ Guardado" : "💾 Guardar"}
          </button>
        </div>
      </div>

      {/* ── ERROR ─────────────────────────────────────────────────────────── */}
      {error && (
        <div style={{
          marginBottom: 12, padding: "10px 14px", borderRadius: 8,
          background: isDark ? "#2d1515" : "#fef2f2",
          border: "1px solid #ef4444", color: "#ef4444", fontSize: 12,
        }}>⚠️ {error}</div>
      )}

      {/* ── LEYENDA ───────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 12, alignItems: "center" }}>
        {OPCIONES.map(o => (
          <div key={o.val} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: txtMuted }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", background: o.color, display: "inline-block", flexShrink: 0 }} />
            <span style={{ fontWeight: 700, color: txtMain }}>{o.val}</span>
            <span>— {o.title}</span>
          </div>
        ))}
        <div style={{ height: 14, width: 1, background: border }} />
        <span style={{ fontSize: 12, color: txtMuted }}>
          <b style={{ color: txtMain }}>TA1–TA5</b> = página por grupo · filtra filas y columnas
        </span>
      </div>

      {/* ── BARRA DE HERRAMIENTAS ─────────────────────────────────────────── */}
      <div style={{
        display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10, alignItems: "center",
        padding: "10px 12px", background: bgPanel, borderRadius: 10, border: `1px solid ${border}`,
      }}>
        {/* Búsqueda */}
        <input
          placeholder="🔍 Buscar campo..."
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
          style={{
            padding: "6px 11px", borderRadius: 7, border: `1px solid ${border}`,
            background: bgInput, color: txtMain, fontSize: 12, width: 180, outline: "none",
          }}
        />

        {/* Filtro grupo */}
        <select value={grupoFiltro} onChange={e => setGrupoFiltro(e.target.value)} style={{
          padding: "6px 10px", borderRadius: 7, border: `1px solid ${border}`,
          background: bgInput, color: txtMain, fontSize: 12, cursor: "pointer", outline: "none",
        }}>
          {grupos.map(g => <option key={g} value={g}>{g === "todos" ? "Todos los grupos" : g}</option>)}
        </select>

        {/* Filtro columna TA — filtra filas Y columnas de tipos */}
        <select value={taColFiltro} onChange={e => { setTaColFiltro(e.target.value); setPaginaFiltro("todas"); setPaginaTipos(0); }} style={{
          padding: "6px 10px", borderRadius: 7,
          border: `1px solid ${taColFiltro !== "todos" ? "#7c3aed" : border}`,
          background: taColFiltro !== "todos" ? (isDark ? "#1a0f35" : "#f5f3ff") : bgInput,
          color: taColFiltro !== "todos" ? "#7c3aed" : txtMain,
          fontSize: 12, cursor: "pointer", outline: "none",
          fontWeight: taColFiltro !== "todos" ? 700 : 400,
        }}>
          <option value="todos">Todos los TA</option>
          <option value="ta1">TA1 — Residencial</option>
          <option value="ta2">TA2 — Suelo</option>
          <option value="ta3">TA3 — Comercial</option>
          <option value="ta4">TA4 — Garaje/Trastero</option>
          <option value="ta5">TA5 — Otros</option>
        </select>
        {taColFiltro !== "todos" && (
          <span style={{ fontSize: 11, color: "#7c3aed", fontWeight: 600 }}>
            Ord./Lín. solo {taColFiltro.toUpperCase()} (vacío = orden global actual)
          </span>
        )}

        {/* Filtro multi Tipo de inmueble */}
        <button
          type="button"
          onClick={() => { setPanelTiposFiltro(v => !v); setPanelTipos(false); }}
          title="Filtrar columnas por uno o varios tipos de inmueble"
          style={{
            padding: "6px 11px", borderRadius: 7,
            border: `1px solid ${tiposInmuebleFiltro.length ? "#ea580c" : border}`,
            background: tiposInmuebleFiltro.length ? (isDark ? "#2d1a0f" : "#fff7ed") : bgInput,
            color: tiposInmuebleFiltro.length ? "#ea580c" : txtMain,
            fontSize: 12, cursor: "pointer",
            fontWeight: tiposInmuebleFiltro.length ? 700 : 600,
          }}
        >
          🏠 Tipo inmueble{tiposInmuebleFiltro.length ? ` (${tiposInmuebleFiltro.length})` : ""}
        </button>

        {/* Filtro página */}
        <select value={paginaFiltro} onChange={e => setPaginaFiltro(e.target.value)} style={{
          padding: "6px 10px", borderRadius: 7, border: `1px solid ${paginaFiltro !== "todas" ? "#2563eb" : border}`,
          background: paginaFiltro !== "todas" ? (isDark ? "#0f1e35" : "#eff6ff") : bgInput,
          color: paginaFiltro !== "todas" ? "#2563eb" : txtMain,
          fontSize: 12, cursor: "pointer", outline: "none", fontWeight: paginaFiltro !== "todas" ? 700 : 400,
        }}>
          <option value="todas">Todas las páginas</option>
          {paginas.filter(p => p !== "todas").map(p => (
            <option key={p} value={p}>{PAGINA_LABELS[p] || `Pág. ${p}`}</option>
          ))}
        </select>

        {/* Filtro visibilidad O/P/S/N */}
        <div style={{ display: "flex", gap: 3, alignItems: "center" }}>
          {[
            { val: "todos", label: "Todos", color: border,    bg: bgInput,                                 txt: txtMuted },
            { val: "O",     label: "O",     color: "#ef4444", bg: isDark ? "#2d1515" : "#fef2f2",          txt: "#ef4444" },
            { val: "P",     label: "P",     color: "#22c55e", bg: isDark ? "#0f2d1e" : "#f0fdf4",          txt: "#16a34a" },
            { val: "S",     label: "S",     color: "#eab308", bg: isDark ? "#2d2510" : "#fefce8",          txt: "#ca8a04" },
            { val: "N",     label: "N",     color: "#94a3b8", bg: isDark ? "#1a1f2e" : "#f1f5f9",          txt: "#64748b" },
          ].map(o => (
            <button key={o.val} onClick={() => setVisibilidadFiltro(o.val)} title={
              o.val === "todos" ? "Sin filtro de visibilidad"
              : o.val === "O" ? "Obligatorio" : o.val === "P" ? "Principal"
              : o.val === "S" ? "Secundario" : "No aplica"
            } style={{
              padding: "5px 9px", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer",
              border: `2px solid ${visibilidadFiltro === o.val ? o.color : border}`,
              background: visibilidadFiltro === o.val ? o.bg : bgInput,
              color: visibilidadFiltro === o.val ? o.txt : txtMuted,
              transition: "all 0.15s",
            }}>{o.label}</button>
          ))}
        </div>

        {/* Filtro TA (filas sin asignación) */}
        <select value={taFiltro} onChange={e => setTaFiltro(e.target.value)} style={{
          padding: "6px 10px", borderRadius: 7, border: `1px solid ${taFiltro !== "todos" ? "#0891b2" : border}`,
          background: taFiltro !== "todos" ? (isDark ? "#0f2020" : "#ecfeff") : bgInput,
          color: taFiltro !== "todos" ? "#0891b2" : txtMuted,
          fontSize: 12, cursor: "pointer", outline: "none", fontWeight: taFiltro !== "todos" ? 700 : 400,
        }}>
          <option value="todos">Asignación TA</option>
          <option value="sin-ta">Sin TA asignado</option>
          {tasDisponibles.filter(t => t !== "todos" && t !== "sin-ta").map(t => (
            <option key={t} value={t}>{t.toUpperCase()} asignado</option>
          ))}
        </select>

        {/* Selector de tipos visibles */}
        <button onClick={() => { setPanelTipos(v => !v); setPanelTiposFiltro(false); }} style={{
          padding: "6px 11px", borderRadius: 7, border: `1px solid ${border}`,
          background: bgInput, color: txtMain, fontSize: 12, cursor: "pointer", fontWeight: 600,
        }}>
          🏷️ Columnas ({tiposVisibles.length}/{tipos.length})
        </button>

        {/* Paginación de columnas de tipos */}
        {totalPagTipos > 1 && (
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            <button onClick={() => setPaginaTipos(p => Math.max(0, p - 1))} disabled={paginaTipos === 0}
              style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${border}`, background: bgInput, color: txtMain, cursor: paginaTipos === 0 ? "not-allowed" : "pointer", fontSize: 14, opacity: paginaTipos === 0 ? 0.4 : 1 }}>‹</button>
            <span style={{ fontSize: 11, color: txtMuted, minWidth: 80, textAlign: "center" }}>
              {tiposTrasFiltroInmueble.length === 0 ? "0" : `${paginaTipos * TIPOS_POR_PAGINA + 1}–${Math.min((paginaTipos + 1) * TIPOS_POR_PAGINA, tiposTrasFiltroInmueble.length)}`} de {tiposTrasFiltroInmueble.length}
            </span>
            <button onClick={() => setPaginaTipos(p => Math.min(totalPagTipos - 1, p + 1))} disabled={paginaTipos >= totalPagTipos - 1}
              style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${border}`, background: bgInput, color: txtMain, cursor: paginaTipos >= totalPagTipos - 1 ? "not-allowed" : "pointer", fontSize: 14, opacity: paginaTipos >= totalPagTipos - 1 ? 0.4 : 1 }}>›</button>
          </div>
        )}

        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: txtMuted }}>{camposFiltrados.length} campo{camposFiltrados.length !== 1 ? "s" : ""}</span>
      </div>

      {/* ── PANEL FILTRO TIPOS DE INMUEBLE (multi) ───────────────────────── */}
      {panelTiposFiltro && (
        <div style={{ marginBottom: 10, padding: "12px 14px", borderRadius: 10, background: bgPanel, border: `1px solid ${tiposInmuebleFiltro.length ? "#ea580c" : border}` }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: txtMuted, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Filtrar por tipo de inmueble (varios a la vez)
            {tiposInmuebleFiltro.length > 0 && (
              <span style={{ color: "#ea580c", marginLeft: 8, fontWeight: 800, textTransform: "none" }}>
                · {tiposInmuebleFiltro.length} seleccionado{tiposInmuebleFiltro.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
            {tipos.map(t => {
              const activo = tiposInmuebleFiltro.includes(t);
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => {
                    setTiposInmuebleFiltro(prev =>
                      activo ? prev.filter(x => x !== t) : [...prev, t]
                    );
                    setPaginaTipos(0);
                  }}
                  style={{
                    padding: "3px 9px", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer",
                    border: `2px solid ${activo ? "#ea580c" : border}`,
                    background: activo ? "rgba(234,88,12,0.12)" : "transparent",
                    color: activo ? "#ea580c" : txtMuted,
                  }}
                >{t}</button>
              );
            })}
          </div>
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => { setTiposInmuebleFiltro([...tipos]); setPaginaTipos(0); }}
              style={{ fontSize: 11, color: "#22c55e", background: "none", border: "none", cursor: "pointer", fontWeight: 700 }}
            >Todos</button>
            <button
              type="button"
              onClick={() => { setTiposInmuebleFiltro([]); setPaginaTipos(0); }}
              style={{ fontSize: 11, color: "#ef4444", background: "none", border: "none", cursor: "pointer", fontWeight: 700 }}
            >Ninguno (= todos visibles)</button>
            {tiposInmuebleFiltro.length > 0 && (
              <span style={{ fontSize: 11, color: txtMuted }}>
                Mostrando: {tiposInmuebleFiltro.join(", ")}
              </span>
            )}
            <button
              type="button"
              onClick={() => setPanelTiposFiltro(false)}
              style={{ marginLeft: "auto", fontSize: 11, color: txtMuted, background: "none", border: "none", cursor: "pointer" }}
            >Cerrar ✕</button>
          </div>
        </div>
      )}

      {/* ── PANEL SELECTOR TIPOS ──────────────────────────────────────────── */}
      {panelTipos && (
        <div style={{ marginBottom: 10, padding: "12px 14px", borderRadius: 10, background: bgPanel, border: `1px solid ${border}` }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: txtMuted, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Tipos visibles en tabla:
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
            {tipos.map(t => {
              const activo = tiposVisibles.includes(t);
              return (
                <button key={t} onClick={() => {
                  setTiposVisibles(prev => activo ? prev.filter(x => x !== t) : [...prev, t]);
                  setPaginaTipos(0);
                }} style={{
                  padding: "3px 9px", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer",
                  border: `2px solid ${activo ? "#2563eb" : border}`,
                  background: activo ? "rgba(37,99,235,0.12)" : "transparent",
                  color: activo ? "#2563eb" : txtMuted,
                }}>{t}</button>
              );
            })}
          </div>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <button onClick={() => { setTiposVisibles([...tipos]); setPaginaTipos(0); }}
              style={{ fontSize: 11, color: "#22c55e", background: "none", border: "none", cursor: "pointer", fontWeight: 700 }}>Todos</button>
            <button onClick={() => { setTiposVisibles([]); setPaginaTipos(0); }}
              style={{ fontSize: 11, color: "#ef4444", background: "none", border: "none", cursor: "pointer", fontWeight: 700 }}>Ninguno</button>
            <button onClick={() => setPanelTipos(false)}
              style={{ marginLeft: "auto", fontSize: 11, color: txtMuted, background: "none", border: "none", cursor: "pointer" }}>Cerrar ✕</button>
          </div>
        </div>
      )}

      {/* ── TABLA ─────────────────────────────────────────────────────────── */}
      <div style={{
        overflowX: "auto",
        overflowY: "auto",
        maxHeight: "calc(100vh - 260px)",
        borderRadius: 10,
        border: `1px solid ${border}`,
        background: bgCard,
      }}>
        <table style={{ borderCollapse: "collapse", width: "100%", tableLayout: "fixed" }}>

          {/* CABECERA */}
          <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
            <tr style={{ background: bgHead }}>

              {/* Columna nombre */}
              <th style={{
                padding: "8px 10px", textAlign: "left", fontSize: 10, fontWeight: 800,
                color: txtMuted, textTransform: "uppercase", letterSpacing: "0.06em",
                position: "sticky", left: 0, top: 0, zIndex: 11, background: bgHead,
                borderRight: `1px solid ${border}`, borderBottom: `2px solid ${border}`,
                whiteSpace: "nowrap", width: 150,
              }}>
                Campo ({camposFiltrados.length})
              </th>

              {/* Columnas tipo */}
              {vePrioridad && tiposEnTabla.map(tipo => (
                <th key={tipo} style={{
                  padding: "4px 1px",
                  borderRight: `1px solid ${border}`,
                  borderBottom: `2px solid ${border}`,
                  verticalAlign: "middle",
                  background: bgHead,
                  width: `calc((100% - 315px) / ${tiposEnTabla.length})`,
                }}>
                  <div style={{
                    fontSize: 8, fontWeight: 800,
                    color: isDark ? "#f1f5f9" : "#1e293b",
                    textTransform: "uppercase", letterSpacing: "0.03em",
                    textAlign: "center", padding: "2px 0", lineHeight: 1.2,
                    overflowWrap: "break-word", wordBreak: "break-word",
                    whiteSpace: "normal", width: "100%",
                  }}>
                    {tipo}
                  </div>
                </th>
              ))}

              {/* Columna Orden */}
              {vePorTa && (
              <th style={{
                padding: "8px 4px", fontSize: 10, fontWeight: 800, color: taOrdenActivo ? "#7c3aed" : txtMuted,
                textTransform: "uppercase", letterSpacing: "0.05em",
                textAlign: "center", whiteSpace: "nowrap", background: bgHead,
                borderRight: `1px solid ${border}`, borderBottom: `2px solid ${border}`, width: 45,
              }} title={taOrdenActivo
                ? `Orden solo ${taOrdenActivo.toUpperCase()} (vacío = usa el orden global)`
                : "Orden global (afecta a todos los TA). Filtra por TA3/TA2… para ordenar solo ese TA"
              }>{taOrdenActivo ? `Ord.${taOrdenActivo.slice(2)}` : "Ord."}</th>
              )}

              {/* Columna Línea */}
              {vePorTa && (
              <th style={{
                padding: "8px 4px", fontSize: 10, fontWeight: 800, color: taOrdenActivo ? "#7c3aed" : "#0891b2",
                textTransform: "uppercase", letterSpacing: "0.05em",
                textAlign: "center", whiteSpace: "nowrap", background: bgHead,
                borderRight: `1px solid ${border}`, borderBottom: `2px solid ${border}`, width: 45,
              }} title={taOrdenActivo
                ? `Línea solo ${taOrdenActivo.toUpperCase()} (vacío = usa la línea global)`
                : "Línea global. Filtra por TA para línea independiente"
              }>{taOrdenActivo ? `Lín.${taOrdenActivo.slice(2)}` : "Lín."}</th>
              )}

              {/* Columna Tipo */}
              <th style={{
                padding: "8px 4px", fontSize: 10, fontWeight: 800, color: "#db2777",
                textTransform: "uppercase", letterSpacing: "0.05em",
                textAlign: "center", whiteSpace: "nowrap", background: bgHead,
                borderRight: `1px solid ${border}`, borderBottom: `2px solid ${border}`, width: 72,
              }}>Tipo</th>

              {/* Columnas TA1-TA5 */}
              {vePaginas && ["ta1","ta2","ta3","ta4","ta5"].map(ta => (
                <th key={ta} style={{
                  padding: "8px 4px", fontSize: 10, fontWeight: 800, color: "#7c3aed",
                  textTransform: "uppercase", letterSpacing: "0.05em",
                  textAlign: "center", whiteSpace: "nowrap", background: bgHead,
                  borderLeft: `1px solid ${border}`, borderBottom: `2px solid ${border}`, width: 52,
                }}>{ta.toUpperCase()}</th>
              ))}
            </tr>
          </thead>

          {/* CUERPO */}
          <tbody>
            {(() => {
              let grupoAnt = null;
              return camposAgrupados.flatMap((campo, idx) => {
                const rows = [];
                const esNuevoGrupo = campo.campo_grupo !== grupoAnt;
                grupoAnt = campo.campo_grupo;
                const colorG = GRUPOS_COLORES[campo.campo_grupo] || "#64748b";

                // Separador de grupo
                if (esNuevoGrupo && campo.campo_grupo) {
                  rows.push(
                    <tr key={`sep-${campo.campo_grupo}`}>
                      <td colSpan={totalColumnasTabla} style={{
                        padding: "5px 10px",
                        background: isDark ? `${colorG}18` : `${colorG}0d`,
                        borderTop: `1px solid ${border}`,
                        borderBottom: `1px solid ${colorG}45`,
                      }}>
                        <span style={{
                          fontSize: 10, fontWeight: 800, color: colorG,
                          textTransform: "uppercase", letterSpacing: "0.08em",
                        }}>▸ {campo.campo_grupo}</span>
                      </td>
                    </tr>
                  );
                }

                const bgFila = idx % 2 === 0
                  ? (isDark ? bgCard : bgCard)
                  : (isDark ? "#141820" : "#fafbfc");

                // ── Fila especial para elementos UI ──────────────────────────
                const TIPOS_UI = ["btn_seccion", "btn_incremento", "btn_selector"];
                if (TIPOS_UI.includes(campo.campo_tipo_input)) {
                  const iconos = {
                    btn_seccion:    { emoji: "🔽", color: "#7c3aed", label: "BOTÓN SECCIÓN" },
                    btn_incremento: { emoji: "±",  color: "#0891b2", label: "BOTONES ±" },
                    btn_selector:   { emoji: "⊞",  color: "#059669", label: "CHIPS SELECTOR" },
                  };
                  const ui = iconos[campo.campo_tipo_input];
                  const subcamposObj = (() => {
                    try { return typeof campo.subcampos === "string" ? JSON.parse(campo.subcampos) : (campo.subcampos || {}); }
                    catch { return {}; }
                  })();
                  const pasos = subcamposObj.pasos || subcamposObj.opciones || [];
                  rows.push(
                    <tr key={campo.id} style={{
                      background: isDark ? `${ui.color}0d` : `${ui.color}08`,
                      borderBottom: `1px dashed ${ui.color}40`,
                    }}>
                      <td style={{
                        padding: "5px 10px",
                        position: "sticky", left: 0, zIndex: 1,
                        background: isDark ? `${ui.color}12` : `${ui.color}0a`,
                        borderRight: `2px dashed ${ui.color}55`,
                        verticalAlign: "middle",
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ fontSize: 13 }}>{ui.emoji}</span>
                          <div>
                            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                              <span style={{
                                fontSize: 8, fontWeight: 800, color: ui.color,
                                background: `${ui.color}20`, padding: "1px 5px",
                                borderRadius: 3, letterSpacing: "0.06em",
                              }}>{ui.label}</span>
                            </div>
                            <div style={{ fontSize: 11, fontWeight: 600, color: txtMain, whiteSpace: "nowrap" }}>
                              {campo.campo_label}
                            </div>
                            <div style={{ fontSize: 9, color: txtMuted }}>{campo.campo_key}</div>
                            {/* Editor de subcampos: chips/combo/dropdown fijos, o pasos de incremento */}
                            {(campo.campo_tipo_input === "btn_selector" || campo.campo_tipo_input === "btn_incremento"
                              || campo.campo_tipo_input === "combobox" || campo.campo_tipo_input === "select_native"
                              || campo.campo_tipo_input === "select") && (
                              <div style={{ marginTop: 4 }}>
                                <div style={{ fontSize: 9, color: txtMuted, marginBottom: 2 }}>
                                  {campo.campo_tipo_input === "btn_incremento"
                                    ? "Pasos +/- (separados por coma):"
                                    : "Opciones (separadas por coma):"}
                                </div>
                                <input
                                  type="text"
                                  placeholder={campo.campo_tipo_input === "btn_incremento"
                                    ? "-10,-1,1,10"
                                    : "Residencial, Terciario, Mixto"}
                                  defaultValue={(() => {
                                    try {
                                      const s = typeof campo.subcampos === "string"
                                        ? JSON.parse(campo.subcampos) : (campo.subcampos || {});
                                      return campo.campo_tipo_input === "btn_incremento"
                                        ? (s.pasos || []).join(", ")
                                        : (s.opciones || []).join(", ");
                                    } catch { return ""; }
                                  })()}
                                  onBlur={e => {
                                    const raw = e.target.value;
                                    const arr = raw.split(",").map(v => v.trim()).filter(Boolean);
                                    const parsed = arr.map(v => isNaN(Number(v)) ? v : Number(v));
                                    const key = campo.campo_tipo_input === "btn_incremento" ? "pasos" : "opciones";
                                    const newSub = { ...((() => {
                                      try { return typeof campo.subcampos === "string"
                                        ? JSON.parse(campo.subcampos) : (campo.subcampos || {}); }
                                      catch { return {}; }
                                    })()), [key]: parsed };
                                    setCampos(prev => prev.map(c => c.id === campo.id
                                      ? { ...c, subcampos: newSub } : c));
                                    setCambiosSinGuardar(true);
                                  }}
                                  style={{
                                    fontSize: 10, padding: "2px 5px", borderRadius: 4,
                                    border: `1px solid ${border}`, background: bgInput,
                                    color: txtMain, width: "100%", boxSizing: "border-box",
                                  }}
                                />
                              </div>
                            )}
                            {pasos.length > 0 && campo.campo_tipo_input !== "btn_selector" && campo.campo_tipo_input !== "btn_incremento" && (
                              <div style={{ display: "flex", gap: 2, marginTop: 2, flexWrap: "wrap" }}>
                                {pasos.map((p, i) => (
                                  <span key={i} style={{
                                    fontSize: 9, padding: "0px 4px", borderRadius: 3,
                                    background: Number(p) < 0
                                      ? (isDark ? "#3b0f0f" : "#fee2e2")
                                      : (isDark ? "#0f2d1e" : "#dcfce7"),
                                    color: Number(p) < 0 ? "#ef4444" : "#16a34a",
                                    fontWeight: 700,
                                  }}>{p > 0 ? `+${p}` : p}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      {vePrioridad && tiposEnTabla.map(tipo => {
                        const val = campo.visibilidad?.[tipo] ?? "N";
                        return (
                          <td key={tipo} style={{
                            padding: "2px 1px", textAlign: "center",
                            borderRight: `1px solid ${border}`, verticalAlign: "middle",
                          }}>
                            <div style={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "center" }}>
                              {OPCIONES.map(op => (
                                <button key={op.val} title={`${tipo}: ${op.title}`}
                                  onClick={() => modPrioridad && cambiarVisibilidad(campo.id, tipo, op.val)}
                                  disabled={!modPrioridad}
                                  style={{
                                    width: 10, height: 10, borderRadius: "50%", border: "none",
                                    cursor: modPrioridad ? "pointer" : "not-allowed", padding: 0,
                                    background: val === op.val ? op.color : (isDark ? "#252d42" : "#e2e8f0"),
                                    transition: "transform 0.1s, background 0.1s",
                                    transform: val === op.val ? "scale(1.2)" : "scale(1)",
                                    flexShrink: 0,
                                    opacity: modPrioridad ? 1 : 0.5,
                                  }}
                                />
                              ))}
                            </div>
                          </td>
                        );
                      })}

                      {vePorTa && (
                      <td style={{ padding: "4px 5px", textAlign: "center", borderRight: `1px solid ${border}`, verticalAlign: "middle" }}>
                        <input type="number" min="1" max="999"
                          value={ordenEfectivo(campo)}
                          onChange={e => cambiarOrden(campo.id, e.target.value)}
                          placeholder="—"
                          disabled={!modPorTa}
                          style={{
                            width: 44, padding: "3px 4px", fontSize: 12, fontWeight: 700,
                            borderRadius: 6, border: `1px solid ${border}`,
                            background: ordenEfectivo(campo) !== "" && ordenEfectivo(campo) != null ? (isDark ? "#1a1f0f" : "#f0fdf4") : bgInput,
                            color: ordenEfectivo(campo) !== "" && ordenEfectivo(campo) != null ? "#16a34a" : txtMuted,
                            textAlign: "center", outline: "none", MozAppearance: "textfield",
                          }}
                        />
                      </td>
                      )}

                      {vePorTa && (
                      <td style={{ padding: "4px 5px", textAlign: "center", borderRight: `1px solid ${border}`, verticalAlign: "middle" }}
                        title="Línea: mismo nº = misma fila. Si saltas números (p. ej. 10→13) la app deja hueco vertical">
                        <input type="number" min="1" max="99"
                          value={lineaEfectiva(campo)}
                          onChange={e => cambiarLinea(campo.id, e.target.value)}
                          placeholder="—"
                          disabled={!modPorTa}
                          style={{
                            width: 44, padding: "3px 4px", fontSize: 12, fontWeight: 700,
                            borderRadius: 6, border: `1px solid ${lineaEfectiva(campo) !== "" && lineaEfectiva(campo) != null ? "#0891b255" : border}`,
                            background: lineaEfectiva(campo) !== "" && lineaEfectiva(campo) != null ? (isDark ? "#0f2020" : "#ecfeff") : bgInput,
                            color: lineaEfectiva(campo) !== "" && lineaEfectiva(campo) != null ? "#0891b2" : txtMuted,
                            textAlign: "center", outline: "none", MozAppearance: "textfield",
                          }}
                        />
                      </td>
                      )}

                      <td style={{ padding: "4px 3px", textAlign: "center", borderRight: `1px solid ${border}`, verticalAlign: "middle" }}>
                        <select value={campo.campo_tipo_input || "text"} onChange={e => cambiarTipoInput(campo.id, e.target.value)}
                          style={{
                            padding: "2px 1px", fontSize: 9, fontWeight: 700,
                            borderRadius: 5, border: `1px solid ${campo.campo_tipo_input === "select_native" ? "#db277755" : border}`,
                            background: campo.campo_tipo_input === "select_native" ? (isDark ? "#2d0f1e" : "#fdf2f8") : bgInput,
                            color: campo.campo_tipo_input === "select_native" ? "#db2777" : txtMuted,
                            cursor: "pointer", outline: "none", width: 68,
                          }}
                        >
                          <option value="boolean">Toggle</option>
                          <option value="number">Número</option>
                          <option value="select">Chips</option>
                          <option value="btn_selector">Chips N+ ⊞</option>
                          <option value="select_native">Dropdown ▾</option>
                          <option value="combobox">Combo ▾✎</option>
                          <option value="date">Fecha 📅</option>
                          <option value="text">Texto</option>
                          <option value="textarea">Área</option>
                          <option value="btn_inventario_ia">Inventario IA 🤖</option>
                          <option value="btn_distribucion">Distribución 📋</option>
                        </select>
                      </td>

                      {vePaginas && ["ta1","ta2","ta3","ta4","ta5"].map(ta => (
                        <td key={ta} style={{ padding: "4px 3px", textAlign: "center", borderLeft: `1px solid ${border}`, verticalAlign: "middle" }}>
                          <select value={campo[ta] || ""} onChange={e => cambiarTAx(campo.id, ta, e.target.value)}
                            disabled={!modPaginas}
                            style={{
                              padding: "2px 2px", fontSize: 10, fontWeight: 700,
                              borderRadius: 5, border: `1px solid ${campo[ta] ? "#7c3aed55" : border}`,
                              background: campo[ta] ? (isDark ? "#1a0f35" : "#f5f3ff") : bgInput,
                              color: campo[ta] ? "#7c3aed" : txtMuted,
                              cursor: modPaginas ? "pointer" : "not-allowed", outline: "none", width: 46,
                            }}
                          >
                            <option value="">—</option>
                            {PAGINAS_ESPECIALES.map(p => (
                              <option key={p} value={p}>{p}</option>
                            ))}
                          </select>
                        </td>
                      ))}
                    </tr>
                  );
                  return rows;
                }
                // ── Fin fila especial ────────────────────────────────────────

                rows.push(
                  <tr key={campo.id} style={{ background: bgFila, borderBottom: `1px solid ${border}` }}>

                    <td style={{
                      padding: "4px 10px",
                      position: "sticky", left: 0, zIndex: 1,
                      background: bgFila,
                      borderRight: `2px solid ${colorG}55`,
                      verticalAlign: "middle",
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <div style={{ width: 3, height: 18, borderRadius: 2, background: colorG, flexShrink: 0 }} />
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: txtMain, whiteSpace: "nowrap" }}>
                            {campo.campo_label}
                            {campo.subcampos?.length > 0 && (
                              <span style={{
                                marginLeft: 5, fontSize: 9, color: txtMuted,
                                background: isDark ? "#1e2535" : "#e2e8f0",
                                padding: "1px 5px", borderRadius: 4,
                              }}>+{campo.subcampos.length} sub</span>
                            )}
                          </div>
                          <div style={{ fontSize: 10, color: txtMuted }}>{campo.campo_key}</div>
                        </div>
                      </div>
                    </td>

                    {vePrioridad && tiposEnTabla.map(tipo => {
                      const val = campo.visibilidad?.[tipo] ?? "N";
                      return (
                        <td key={tipo} style={{
                          padding: "2px 1px", textAlign: "center",
                          borderRight: `1px solid ${border}`, verticalAlign: "middle",
                        }}>
                          <div style={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "center" }}>
                            {OPCIONES.map(op => (
                              <button key={op.val} title={`${tipo}: ${op.title}`}
                                onClick={() => modPrioridad && cambiarVisibilidad(campo.id, tipo, op.val)}
                                disabled={!modPrioridad}
                                style={{
                                  width: 10, height: 10, borderRadius: "50%", border: "none",
                                  cursor: modPrioridad ? "pointer" : "not-allowed", padding: 0,
                                  background: val === op.val ? op.color : (isDark ? "#252d42" : "#e2e8f0"),
                                  transition: "transform 0.1s, background 0.1s",
                                  transform: val === op.val ? "scale(1.2)" : "scale(1)",
                                  flexShrink: 0,
                                  opacity: modPrioridad ? 1 : 0.5,
                                }}
                              />
                            ))}
                          </div>
                        </td>
                      );
                    })}

                    {vePorTa && (
                    <td style={{ padding: "4px 5px", textAlign: "center", borderRight: `1px solid ${border}`, verticalAlign: "middle" }}>
                      <input type="number" min="1" max="999"
                        value={ordenEfectivo(campo)}
                        onChange={e => cambiarOrden(campo.id, e.target.value)}
                        placeholder="—"
                        disabled={!modPorTa}
                        style={{
                          width: 44, padding: "3px 4px", fontSize: 12, fontWeight: 700,
                          borderRadius: 6, border: `1px solid ${border}`,
                          background: ordenEfectivo(campo) !== "" && ordenEfectivo(campo) != null ? (isDark ? "#1a1f0f" : "#f0fdf4") : bgInput,
                          color: ordenEfectivo(campo) !== "" && ordenEfectivo(campo) != null ? "#16a34a" : txtMuted,
                          textAlign: "center", outline: "none", MozAppearance: "textfield",
                        }}
                      />
                    </td>
                    )}

                    {vePorTa && (
                    <td style={{ padding: "4px 5px", textAlign: "center", borderRight: `1px solid ${border}`, verticalAlign: "middle" }}
                      title="Línea: mismo nº = misma fila. Si saltas números (p. ej. 10→13) la app deja hueco vertical">
                      <input type="number" min="1" max="99"
                        value={lineaEfectiva(campo)}
                        onChange={e => cambiarLinea(campo.id, e.target.value)}
                        placeholder="—"
                        disabled={!modPorTa}
                        style={{
                          width: 44, padding: "3px 4px", fontSize: 12, fontWeight: 700,
                          borderRadius: 6, border: `1px solid ${lineaEfectiva(campo) !== "" && lineaEfectiva(campo) != null ? "#0891b255" : border}`,
                          background: lineaEfectiva(campo) !== "" && lineaEfectiva(campo) != null ? (isDark ? "#0f2020" : "#ecfeff") : bgInput,
                          color: lineaEfectiva(campo) !== "" && lineaEfectiva(campo) != null ? "#0891b2" : txtMuted,
                          textAlign: "center", outline: "none", MozAppearance: "textfield",
                        }}
                      />
                    </td>
                    )}

                    <td style={{ padding: "4px 3px", textAlign: "center", borderRight: `1px solid ${border}`, verticalAlign: "middle" }}>
                      <select value={campo.campo_tipo_input || "text"} onChange={e => cambiarTipoInput(campo.id, e.target.value)}
                        style={{
                          padding: "2px 1px", fontSize: 9, fontWeight: 700,
                          borderRadius: 5, border: `1px solid ${campo.campo_tipo_input === "select_native" ? "#db277755" : border}`,
                          background: campo.campo_tipo_input === "select_native" ? (isDark ? "#2d0f1e" : "#fdf2f8") : bgInput,
                          color: campo.campo_tipo_input === "select_native" ? "#db2777" : txtMuted,
                          cursor: "pointer", outline: "none", width: 68,
                        }}
                      >
                        <option value="boolean">Toggle</option>
                        <option value="number">Número</option>
                          <option value="select">Chips</option>
                          <option value="btn_selector">Chips N+ ⊞</option>
                          <option value="select_native">Dropdown ▾</option>
                          <option value="combobox">Combo ▾✎</option>
                          <option value="date">Fecha 📅</option>
                          <option value="text">Texto</option>
                          <option value="textarea">Área</option>
                          <option value="btn_inventario_ia">Inventario IA 🤖</option>
                          <option value="btn_distribucion">Distribución 📋</option>
                        </select>
                      </td>

                      {vePaginas && ["ta1","ta2","ta3","ta4","ta5"].map(ta => (
                      <td key={ta} style={{ padding: "4px 3px", textAlign: "center", borderLeft: `1px solid ${border}`, verticalAlign: "middle" }}>
                        <select value={campo[ta] || ""} onChange={e => cambiarTAx(campo.id, ta, e.target.value)}
                          disabled={!modPaginas}
                          style={{
                            padding: "2px 2px", fontSize: 10, fontWeight: 700,
                            borderRadius: 5, border: `1px solid ${campo[ta] ? "#7c3aed55" : border}`,
                            background: campo[ta] ? (isDark ? "#1a0f35" : "#f5f3ff") : bgInput,
                            color: campo[ta] ? "#7c3aed" : txtMuted,
                            cursor: modPaginas ? "pointer" : "not-allowed", outline: "none", width: 46,
                          }}
                        >
                          <option value="">—</option>
                          {PAGINAS_ESPECIALES.map(p => (
                            <option key={p} value={p}>{p}</option>
                          ))}
                        </select>
                      </td>
                    ))}
                  </tr>
                );

                return rows;
              });
            })()}
          </tbody>
        </table>

        {camposFiltrados.length === 0 && (
          <div style={{ padding: 32, textAlign: "center", color: txtMuted, fontSize: 13 }}>
            No hay campos que coincidan con los filtros.
          </div>
        )}
      </div>

      {/* ── PIE ────────────────────────────────────────────────────────────── */}
      <div style={{ marginTop: 12, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ fontSize: 11, color: txtMuted }}>
          💡 Guardado en Railway · Caché local: <code style={{ fontSize: 10 }}>vitalcasa_campos_config_v2</code>
        </div>
        <div style={{ flex: 1 }} />
        {cambiosSinGuardar && (
          <button onClick={guardar} disabled={guardando} style={{
            padding: "8px 20px", borderRadius: 8, border: "none",
            background: "#f59e0b", color: "#fff", fontSize: 13, fontWeight: 700,
            cursor: guardando ? "not-allowed" : "pointer",
          }}>
            {guardando ? "Guardando..." : "💾 Guardar cambios"}
          </button>
        )}
      </div>
    </div>
  );
}
